// frontend/src/services/batchService.ts

import axiosInstance from '../config/axiosInstance';

export interface BatchJob {
  job_id: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'COMPLETED_WITH_ERRORS';
  created_at: string;
  updated_at: string;
  total_rows: number | null;
  processed_rows: number;
  output_file_path: string | null;
  error_message: string | null;
  input_file_path: string;
}

export interface BatchJobUploadResponse {
  job_id: string;
  message: string;
}

export const uploadBatchFile = async (
    file: File,
    mapping: { customer_id: string; product_id: string; },
    config: {
      strategy_profile: string;
      strategy_sensitivity: number;
      sheet_name?: string | null;
      header_row_index?: number | null;
    },
): Promise<BatchJobUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('column_mapping', JSON.stringify(mapping));
  formData.append('strategy_profile', config.strategy_profile);
  formData.append('strategy_sensitivity', config.strategy_sensitivity.toString());

  const sheetName = config.sheet_name?.trim() || null;
  const hasSheetName = sheetName !== null;
  const hasHeaderRowIndex =
    config.header_row_index !== null &&
    config.header_row_index !== undefined;

  if (hasSheetName !== hasHeaderRowIndex) {
    throw new Error(
      'Configuración estructural XLSX incompleta: hoja y fila de cabecera deben enviarse conjuntamente.',
    );
  }

  if (hasSheetName && hasHeaderRowIndex) {
    formData.append('sheet_name', sheetName);
    formData.append('header_row_index', String(config.header_row_index));
  }

  try {
    // La petición a '/batch/upload' se convertirá en '/api/v1/batch/upload'.
    const response = await axiosInstance.post<BatchJobUploadResponse>('/batch/upload', formData);
    return response.data;
  } catch (error: any) {
    const errorDetail = error.response?.data?.detail || 'Error al iniciar el trabajo de optimización.';
    throw new Error(errorDetail);
  }
};

export const getJobStatus = async (jobId: string): Promise<BatchJob> => {
  try {
    const response = await axiosInstance.get<BatchJob>(`/batch/jobs/${jobId}/status`);
    return response.data;
  } catch (error: any) {
    const errorDetail = error.response?.data?.detail || `Error al obtener el estado del trabajo ${jobId}.`;
    throw new Error(errorDetail);
  }
};

export const getJobsList = async (): Promise<BatchJob[]> => {
  try {
    const response = await axiosInstance.get<BatchJob[]>('/batch/jobs');
    return response.data;
  } catch (error: any) {
    const errorDetail = error.response?.data?.detail || 'Error al obtener la lista de trabajos.';
    throw new Error(errorDetail);
  }
};

export const downloadJobResult = async (jobId: string): Promise<void> => {
  try {
    const response = await axiosInstance.get(`/batch/jobs/${jobId}/download`, {
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = `job_${jobId}_results.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1];
      }
    }

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    if (error.response?.data instanceof Blob) {
      try {
        const errorText = await error.response.data.text();
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.detail || 'Error al descargar el archivo.');
      } catch (e) {
         throw new Error('Error no estructurado al descargar el archivo.');
      }
    }
    const errorDetail = error.response?.data?.detail || `Error al descargar el resultado del trabajo.`;
    throw new Error(errorDetail);
  }
};

export const deleteJob = async (jobId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/batch/jobs/${jobId}`);
  } catch (error: any) {
    const errorDetail = error.response?.data?.detail || `Error al eliminar el trabajo ${jobId}.`;
    throw new Error(errorDetail);
  }
};

// --- INICIO DE LA MODIFICACIÓN ---
export const reOptimizeJob = async (jobId: string): Promise<BatchJobUploadResponse> => {
  try {
    const response = await axiosInstance.post<BatchJobUploadResponse>(`/batch/jobs/${jobId}/re-optimize`);
    return response.data;
  } catch (error: any) {
    const errorDetail = error.response?.data?.detail || `Error al re-optimizar el trabajo ${jobId}.`;
    throw new Error(errorDetail);
  }
};
// --- FIN DE LA MODIFICACIÓN ---